import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import type { User } from '@core/account-types';
import type { FullCandidate, ShareOnTheGoRequestPayload } from '@core/candidate-types';
import { I18nService } from '@core/i18n-legacy';
import { InterviewResultRequestActions } from '@core/interview-result-request-data';
import type { Placement } from '@core/placements-types';
import { NotificationsController } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

@Injectable({ providedIn: 'root' })
export class RequestEvaluationService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly i18n = inject(I18nService);
  private readonly notificationsCtrl = inject(NotificationsController);

  public createInterviewRequest({
    candidate,
    templateId,
    offerId,
    talentPoolId,
    admins,
    dueDate,
    shareOnTheGoRequestPayload,
  }: {
    candidate: FullCandidate;
    templateId: number;
    offerId?: number;
    talentPoolId?: number;
    admins: User[];
    dueDate?: string | null;
    shareOnTheGoRequestPayload: ShareOnTheGoRequestPayload;
  }): void {
    const placement = this.findPlacement(candidate, offerId, talentPoolId);
    const stageId = this.getStageId(placement);
    const eligibleAdmins = this.filterUsersToRequestEvalFrom({
      candidate,
      users: admins,
      shareOnTheGoRequestPayload,
      offerId,
      talentPoolId,
    });

    const data = eligibleAdmins.map(admin => ({
      adminId: admin.id,
      offerId,
      talentPoolId,
      stageId,
      interviewTemplateId: templateId,
      dueDate: dueDate ?? null,
    }));

    const correlationId = generateUuid();

    this.notifyOnRequestSent(correlationId);

    this.store.dispatch(
      InterviewResultRequestActions.createOnePessimistic({
        payload: {
          candidateId: candidate.id,
          data,
          shareOnTheGoRequestPayload,
          correlationId,
        },
      }),
    );
  }

  private notifyOnRequestSent(correlationId: string): void {
    merge(
      this.actions$.pipe(
        ofType(InterviewResultRequestActions.createOnePessimisticSuccess),
        filter(({ payload }) => payload.correlationId === correlationId),
        map(() => true),
      ),
      this.actions$.pipe(
        ofType(InterviewResultRequestActions.createOnePessimisticFailed),
        filter(({ payload }) => payload.correlationId === correlationId),
        map(() => false),
      ),
    )
      .pipe(take(1))
      .subscribe(success => {
        if (success) {
          this.notificationsCtrl.success(
            this.i18n.translate('app.interview.request_send_notification'),
          );
        }
      });
  }

  private findPlacement(
    candidate: FullCandidate,
    offerId?: number,
    talentPoolId?: number,
  ): Placement | undefined {
    return candidate.placements.find(placement =>
      offerId ? placement.offerId === offerId : placement.talentPoolId === talentPoolId,
    );
  }

  private getStageId(placement: Placement | undefined): number | null {
    return placement?.offer?.kind === 'job' && placement.stage?.id ? placement.stage.id : null;
  }

  private filterUsersToRequestEvalFrom({
    candidate,
    users,
    shareOnTheGoRequestPayload,
    offerId,
    talentPoolId,
  }: {
    candidate: FullCandidate;
    users: User[];
    shareOnTheGoRequestPayload: ShareOnTheGoRequestPayload;
    offerId?: number;
    talentPoolId?: number;
  }): User[] {
    return users.filter(
      user =>
        shareOnTheGoRequestPayload.grantAccess.find(entry => entry.adminId === user.id) ||
        user.roleAbilities.includes(Ability.ViewAllJobs) ||
        (offerId && user.offerIds?.includes(offerId)) ||
        (talentPoolId && user.talentPoolIds?.includes(talentPoolId)) ||
        user.accessiblePlacementIds?.includes(
          this.findPlacement(candidate, offerId, talentPoolId)?.id ?? -1,
        ),
    );
  }
}
